import React from 'react';
import PropTypes from 'prop-types';
import { getPlatformSpecs, splitIntoThreads, isThreadFormat } from '../../constants/platformSpecs';
import CharacterCounter from './CharacterCounter';
import ImageCropPreview from './ImageCropPreview';
import styles from './XPreview.module.scss';

const XPreview = ({
  user,
  content,
  imageUrl,
  onRemoveImage,
}) => {
  const specs = getPlatformSpecs('x');

  const stripHtml = (html) => {
    if (!html) return '';
    // Convert block elements to newlines before stripping
    let text = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/div>/gi, '\n')
      .replace(/<div[^>]*>/gi, '')
      .replace(/<\/li>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ');

    // Strip remaining HTML tags
    const tmp = document.createElement('DIV');
    tmp.innerHTML = text;
    text = tmp.textContent || tmp.innerText || '';

    // Clean up excessive newlines
    return text.replace(/\n{3,}/g, '\n\n').trim();
  };

  const plainContent = stripHtml(content);
  const charCount = plainContent.length;

  // Always use splitIntoThreads to handle both delimiter format and overflow
  const threads = splitIntoThreads(plainContent, specs.characterLimit);
  const isThread = threads.length > 1;
  const hasDelimiters = isThreadFormat(plainContent);

  const getUserName = () => {
    if (!user) return 'User';
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.name || 'User';
  };

  const getHandle = () => {
    if (!user) return '@user';
    const name = getUserName().toLowerCase().replace(/\s+/g, '');
    return `@${name}`;
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const firstName = user.first_name || user.name?.split(' ')[0] || '';
    const lastName = user.last_name || user.name?.split(' ')[1] || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  const renderTweet = (text, index, isFirst = false, isLast = false, total = 1) => (
    <div
      key={index}
      className={`${styles.tweet} ${!isFirst ? styles.threadReply : ''}`}
    >
      {/* Thread connector line */}
      {!isFirst && (
        <div className={styles.threadLine} />
      )}

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.avatar}>
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt={getUserName()} />
          ) : (
            <span>{getUserInitials()}</span>
          )}
        </div>
        <div className={styles.headerContent}>
          <div className={styles.userRow}>
            <span className={styles.userName}>{getUserName()}</span>
            <span className={styles.verifiedBadge}>
              <svg viewBox="0 0 22 22" fill="currentColor">
                <path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.688-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.687.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681s.075-1.299-.165-1.903c.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z" />
              </svg>
            </span>
            <span className={styles.handle}>{getHandle()}</span>
            <span className={styles.dot}>·</span>
            <span className={styles.timestamp}>now</span>
          </div>
          {total > 1 && (
            <div className={styles.threadIndicator}>
              {index + 1}/{total}
            </div>
          )}
        </div>
        <button className={styles.moreButton} aria-label="More options">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className={styles.content}>
        <div className={styles.text}>{text}</div>
      </div>

      {/* Image (only on first tweet) */}
      {isFirst && imageUrl && (
        <div className={styles.imageContainer}>
          <ImageCropPreview
            imageUrl={imageUrl}
            platformType="x"
            onRemove={onRemoveImage}
          />
        </div>
      )}

      {/* Action Bar */}
      <div className={styles.actionBar}>
        <button className={styles.actionButton}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M1.751 10c0-4.42 3.584-8 8.005-8h4.366c4.49 0 8.129 3.64 8.129 8.13 0 2.96-1.607 5.68-4.196 7.11l-8.054 4.46v-3.69h-.067c-4.49.1-8.183-3.51-8.183-8.01zm8.005-6c-3.317 0-6.005 2.69-6.005 6 0 3.37 2.77 6.08 6.138 6.01l.351-.01h1.761v2.3l5.087-2.81c1.951-1.08 3.163-3.13 3.163-5.36 0-3.39-2.744-6.13-6.129-6.13H9.756z" />
          </svg>
        </button>
        <button className={styles.actionButton}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M4.5 3.88l4.432 4.14-1.364 1.46L5.5 7.55V16c0 1.1.896 2 2 2H13v2H7.5c-2.209 0-4-1.79-4-4V7.55L1.432 9.48.068 8.02 4.5 3.88zM16.5 6H11V4h5.5c2.209 0 4 1.79 4 4v8.45l2.068-1.93 1.364 1.46-4.432 4.14-4.432-4.14 1.364-1.46 2.068 1.93V8c0-1.1-.896-2-2-2z" />
          </svg>
        </button>
        <button className={styles.actionButton}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M16.697 5.5c-1.222-.06-2.679.51-3.89 2.16l-.805 1.09-.806-1.09C9.984 6.01 8.526 5.44 7.304 5.5c-1.243.07-2.349.78-2.91 1.91-.552 1.12-.633 2.78.479 4.82 1.074 1.97 3.257 4.27 7.129 6.61 3.87-2.34 6.052-4.64 7.126-6.61 1.111-2.04 1.03-3.7.477-4.82-.561-1.13-1.666-1.84-2.908-1.91zm4.187 7.69c-1.351 2.48-4.001 5.12-8.379 7.67l-.503.3-.504-.3c-4.379-2.55-7.029-5.19-8.382-7.67-1.36-2.5-1.41-4.86-.514-6.67.887-1.79 2.647-2.91 4.601-3.01 1.651-.09 3.368.56 4.798 2.01 1.429-1.45 3.146-2.1 4.796-2.01 1.954.1 3.714 1.22 4.601 3.01.896 1.81.846 4.17-.514 6.67z" />
          </svg>
        </button>
        <button className={styles.actionButton}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M8.75 21V3h2v18h-2zM18 21V8.5h2V21h-2zM4 21l.004-10h2L6 21H4zm9.248 0v-7h2v7h-2z" />
          </svg>
        </button>
        <button className={styles.actionButton}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.59l5.7 5.7-1.41 1.42L13 6.41V16h-2V6.41l-3.3 3.3-1.41-1.42L12 2.59zM21 15l-.001 3.995A2.005 2.005 0 0119 21H5a2.006 2.006 0 01-2-2.005L3 15h2l.001 4h14L19 15h2z" />
          </svg>
        </button>
      </div>
    </div>
  );

  return (
    <div className={styles.xPreview}>
      {/* Thread indicator if multiple tweets */}
      {isThread && (
        <div className={styles.threadHeader}>
          <svg viewBox="0 0 24 24" fill="currentColor" className={styles.threadIcon}>
            <path d="M8.52 3.59l.15.06c2.48 1.17 4.05 3.07 4.75 5.26-.59-.21-1.21-.33-1.85-.33-2.58 0-4.72 1.85-5.18 4.28l-.15-.06c-2.48-1.17-4.05-3.07-4.75-5.26.59.21 1.21.33 1.85.33 2.58 0 4.72-1.85 5.18-4.28zm.01 11.21l.15.06c1.58.75 2.9 1.85 3.88 3.21-.96-.33-1.97-.52-3.02-.52-2.77 0-5.26 1.23-6.94 3.17l-.15-.06c-1.58-.75-2.9-1.85-3.88-3.21.96.33 1.97.52 3.02.52 2.77 0 5.26-1.23 6.94-3.17zm6.34-10.78c.58 1.58 1.53 2.96 2.75 4.06-1.34-.54-2.79-.83-4.3-.83-.88 0-1.74.1-2.56.28.14-.96.22-1.95.22-2.95 0-1.05-.12-2.07-.34-3.05 1.36.54 2.82.83 4.33.83.64 0 1.27-.06 1.88-.17-.14.62-.13 1.24.02 1.83z" />
          </svg>
          <span>Thread ({threads.length} posts)</span>
        </div>
      )}

      {/* Render tweets */}
      <div className={styles.tweetsContainer}>
        {threads.map((text, index) =>
          renderTweet(text, index, index === 0, index === threads.length - 1, threads.length)
        )}
      </div>

      {/* Character Counter - at bottom for consistency */}
      <div className={styles.counterSection}>
        <CharacterCounter
          charCount={charCount}
          platformType="x"
          isThread={isThread}
          threadCount={threads.length}
        />
      </div>
    </div>
  );
};

XPreview.propTypes = {
  user: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    name: PropTypes.string,
    avatar_url: PropTypes.string,
  }),
  content: PropTypes.string,
  imageUrl: PropTypes.string,
  onRemoveImage: PropTypes.func,
};

export default XPreview;
